import React, { memo } from 'react'
import cn, { type Argument as ClassName } from 'classnames'
import { Card } from 'primereact/card'

import useText from '../../../common/hooks/useText'
import type { PaymentOrganizationSubscriptionResponse } from '../../../common/redux/api/web-api'
import displayDateTime from '../../../lib/displayDateTime'

export type BillingPlanInterval = 'month' | 'year'

export type BillingPlanWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    subscription: PaymentOrganizationSubscriptionResponse
    interval?: BillingPlanInterval
}

const BILLING_PLAN_INTERVAL_TEXT_KEYS: Record<BillingPlanInterval, string> = {
    month: 'lblBillingIntervalMonthly',
    year: 'lblBillingIntervalYearly',
}

export const BillingPlanWidget = memo(function BillingPlanWidget({
    className,
    style,
    subscription,
    interval,
}: BillingPlanWidgetProps): React.ReactElement {
    const text = useText()
    const intervalLabel = interval ? text(BILLING_PLAN_INTERVAL_TEXT_KEYS[interval]) : null
    const cancellationDate = subscription.cancelAt ? displayDateTime(subscription.cancelAt) : null
    const activationDate = subscription.createdAtUtc ? displayDateTime(subscription.createdAtUtc) : null

    return (
        <Card
            className={cn(className)}
            style={style}
            pt={{ body: { className: 'p-4' }, content: { className: 'p-0' } }}
        >
            <div className="flex flex-column gap-5">
                <div className="text-xl font-bold">{text('lblYourPlan')}</div>

                <div className="flex flex-column gap-2">
                    <h1 className="m-0">{subscription.name}</h1>
                    {intervalLabel ? (
                        <p data-testid="billing-plan-interval" className="m-0 text-sm text-600">
                            {text('lblBillingInterval')}: {intervalLabel}
                        </p>
                    ) : null}
                </div>

                {cancellationDate ? (
                    <div className="flex flex-column align-items-start gap-3">
                        <div
                            data-testid="billing-plan-cancelled-panel"
                            className="inline-flex align-self-start align-items-start gap-2 border-round bg-red-100 px-2 py-1"
                        >
                            <i className="pi pi-times mt-1 text-xs text-red-700" aria-hidden="true" />
                            <p className="m-0 text-sm line-height-3 text-red-700">
                                {text('lblThisPlanHasBeenCancelled', {
                                    date: cancellationDate,
                                })}
                            </p>
                        </div>

                        <p className="m-0">{text('lblIfYouWishToAbortCancel')}</p>
                    </div>
                ) : null}

                {activationDate ? (
                    <div
                        data-testid="billing-plan-activated-panel"
                        className="inline-flex align-self-start align-items-start gap-2 border-round bg-green-100 px-2 py-1"
                    >
                        <i className="pi pi-check mt-1 text-xs text-green-700" aria-hidden="true" />
                        <p className="m-0 text-sm line-height-3 text-700">
                            {text('lblActivated')} {activationDate}
                        </p>
                    </div>
                ) : null}
            </div>
        </Card>
    )
})
